import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    MatchableItem,
    AccrualMatchTableCallbacks,
    AccrualMatchSection,
} from "../types";
import {
    MATCH_ITEM_LOCKED_TOOLTIP,
    useIsMatchableItemLocked,
} from "../matchLock";

interface AccrualMatchTableOptions {
    section: AccrualMatchSection;
    actionLabel?: string;
    isLockGatingEnabled?: boolean;
}

type AccrualMatchMeta = ViewTableMeta<
    AccrualMatchTableCallbacks,
    AccrualMatchTableOptions
>;

export const ActionCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, MatchableItem, unknown>) => {
        const meta = table.options.meta as AccrualMatchMeta | undefined;
        const section = meta?.options?.section;
        const onAction = meta?.callbacks?.onAction;

        // Locked-period gating (PRO-12200): a locked row can't be matched
        // (search/top-table "Match") or unmatched ("selected" row's
        // "Unmatch") from here — the lock icon in the LOCK column already
        // explains why the row itself is locked, so the disabled-button
        // tooltip only needs the generic locked-period copy. Called
        // unconditionally (before the "source" early return below) so hook
        // order can't vary between renders of the same row. Only applied where
        // the LOCK column is shown (see `isLockGatingEnabled`), so the legacy
        // reverse-match drawer view — which has no lock icon — doesn't disable
        // an action with no visible explanation.
        const isRowLocked = useIsMatchableItemLocked(row.original);

        if (section === "source" || !section) {
            return <ViewTableCell />;
        }

        const defaultPrimaryLabel =
            section === "selected" ? "Unmatch" : "Match";
        const primaryLabel = meta?.options?.actionLabel ?? defaultPrimaryLabel;
        const isPrimaryDisabled =
            (meta?.options?.isLockGatingEnabled ?? false) && isRowLocked;

        return (
            <ViewTableCell captureClicks>
                <HStack
                    gap={12}
                    justify="end"
                    className={viewTableStyles.showOnHover}
                >
                    <Tooltip
                        content={
                            isPrimaryDisabled
                                ? MATCH_ITEM_LOCKED_TOOLTIP
                                : undefined
                        }
                    >
                        <Button
                            variant="primary"
                            size="md"
                            disabled={isPrimaryDisabled}
                            onClick={() => onAction?.(row.original)}
                        >
                            {primaryLabel}
                        </Button>
                    </Tooltip>
                </HStack>
            </ViewTableCell>
        );
    },
);
ActionCell.displayName = "ActionCell";
